import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  Stack,
  Select,
  Checkbox,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
  Grid,
  GridItem,
  Badge,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import {
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
} from '@chakra-ui/react';
import { FiDownload, FiFileText, FiArchive } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import * as nominationService from '../../services/api/nominationService';
import reportService from '../../services/api/reportService';
import config from '../../config/config';
import { getNominationStatusColor, getNominationStatusLabel } from '../../utils/evaluationStatusUtils';
// Charts
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

const Reports = () => {
  const { currentUser } = useAuth();
  const isCommittee = currentUser?.role === 'committee';

  // Get user data directly from localStorage for more reliable branch access
  const getUserDataFromStorage = () => {
    try {
      const userDataString = localStorage.getItem('userData');
      if (userDataString) {
        return JSON.parse(userDataString);
      }
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
    }
    return null;
  };

  // Note: per-center stats are now aggregated by backend; no local center list is needed

  // Fetch per-center statistics table (single backend call)
  const fetchCenterStatistics = async () => {
    setCenterStatsLoading(true);
    setCenterStatsError(null);
    try {
      const branchParam = selectedBranch || undefined;
      const res = await reportService.getCentersStatistics(branchParam);
      let rows = Array.isArray(res?.data) ? res.data : [];
      // If a specific center is selected, filter client-side by code
      if (selectedCenter) {
        rows = rows.filter(r => r.center_code === selectedCenter);
      }
      // Map to UI row shape
      const mapped = rows.map(r => {
        const sc = r.status_counts || {};
        return {
          id: r.center_code,
          name: r.center_name,
          nom_total: r.total ?? ((sc.draft||0)+(sc.submitted||0)+(sc.under_review||0)+(sc.shortlisted||0)+(sc.rejected||0)+(sc.accepted||0)),
          draft: sc.draft || 0,
          submitted: sc.submitted || 0,
          under_review: sc.under_review || 0,
          shortlisted: sc.shortlisted || 0,
          rejected: sc.rejected || 0,
          accepted: sc.accepted || 0,
        };
      });
      setCenterRows(mapped);
      // Compute table totals
      const totals = mapped.reduce((acc, r) => {
        acc.nom_total += r.nom_total || 0;
        acc.draft += r.draft || 0;
        acc.submitted += r.submitted || 0;
        acc.under_review += r.under_review || 0;
        acc.shortlisted += r.shortlisted || 0;
        acc.rejected += r.rejected || 0;
        acc.accepted += r.accepted || 0;
        return acc;
      }, { nom_total: 0, draft: 0, submitted: 0, under_review: 0, shortlisted: 0, rejected: 0, accepted: 0 });
      setCenterTotals(totals);
    } catch (e) {
      console.error('Error fetching per-center statistics:', e);
      setCenterStatsError('تعذر تحميل جدول المراكز. حاول مرة أخرى.');
    } finally {
      setCenterStatsLoading(false);
    }
  };

  // Fetch statistics for the Statistical Reports tab
  const fetchStatistics = async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const filters = {
        branchId: selectedBranch || undefined,
        centerId: selectedCenter || undefined,
      };
      const [nomRes, evalRes] = await Promise.all([
        reportService.getNominationsStatistics(filters),
        reportService.getEvaluationsStatistics(filters),
      ]);
      setNominationsStats(nomRes.data);
      setEvaluationsStats(evalRes.data);
    } catch (e) {
      console.error('Error fetching statistics:', e);
      setStatsError('حدث خطأ أثناء تحميل الإحصائيات. يرجى المحاولة مرة أخرى.');
    } finally {
      setStatsLoading(false);
    }
  };

  const userDataFromStorage = getUserDataFromStorage();
  const toast = useToast();

  // State for filters
  const [branches, setBranches] = useState([]);
  const [centers, setCenters] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedCenter, setSelectedCenter] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  // State for report options
  const [includeEvaluation, setIncludeEvaluation] = useState(false);
  const [includeAttachmentA, setIncludeAttachmentA] = useState(false);
  const [includeAttachmentB, setIncludeAttachmentB] = useState(false);
  const [allFields, setAllFields] = useState(false);
  const [reportFormat, setReportFormat] = useState('xlsx');

  // State for nominations
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Loading states for downloads
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [downloadingNominationId, setDownloadingNominationId] = useState(null);

  // State for statistics
  const [nominationsStats, setNominationsStats] = useState(null);
  const [evaluationsStats, setEvaluationsStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  // Per-center table state
  const [centerRows, setCenterRows] = useState([]);
  const [centerStatsLoading, setCenterStatsLoading] = useState(false);
  const [centerStatsError, setCenterStatsError] = useState(null);
  const [centerTotals, setCenterTotals] = useState({ nom_total: 0, draft: 0, submitted: 0, under_review: 0, shortlisted: 0, rejected: 0, accepted: 0 });

  // Use static configuration for branches and centers
  useEffect(() => {
    // Set branches and centers from static config
    const cfgBranches = config.organization.branches || [];
    const cfgCenters = config.organization.centers || [];
    setBranches(cfgBranches);
    setCenters(cfgCenters);

    // If committee user, set and lock branch to their assigned branch (use branch ID)
    if (isCommittee) {
      // Resolve branch ID from available user data (branch_code preferred, fallback to branch_id)
      let resolvedBranchId = null;
      const fromStorageCode = userDataFromStorage?.branch_code;
      const fromStorageId = userDataFromStorage?.branch_id;
      const fromUserCode = currentUser?.branch_code;
      const fromUserId = currentUser?.branch_id;

      if (fromStorageCode) {
        const match = cfgBranches.find(b => String(b.id) === String(fromStorageCode));
        if (match) resolvedBranchId = match.id; // in config, id is the branch code (e.g., 'makkah')
      }
      if (!resolvedBranchId && fromUserCode) {
        const match = cfgBranches.find(b => String(b.id) === String(fromUserCode));
        if (match) resolvedBranchId = match.id; // same logic as above
      }
      if (!resolvedBranchId && fromStorageId) {
        const match = cfgBranches.find(b => String(b.id) === String(fromStorageId));
        resolvedBranchId = match ? match.id : fromStorageId;
      }
      if (!resolvedBranchId && fromUserId) {
        const match = cfgBranches.find(b => String(b.id) === String(fromUserId));
        resolvedBranchId = match ? match.id : fromUserId;
      }

      if (resolvedBranchId) {
        setSelectedBranch(String(resolvedBranchId));
      } else {
        console.warn('Could not resolve committee branch ID from user data', { currentUser, userDataFromStorage });
        // Fallback: default to first configured branch so centers become selectable
        if (cfgBranches.length > 0) {
          const fallbackId = String(cfgBranches[0].id);
          console.log('Falling back to first configured branch ID:', fallbackId);
          setSelectedBranch(fallbackId);
        }
      }
    }
  }, [isCommittee, currentUser, userDataFromStorage]);

  // Keep selectedCenter consistent with selectedBranch
  useEffect(() => {
    if (!selectedBranch) return;
    const validCenters = (centers || []).filter(c => String(c.branchId) === String(selectedBranch));
    if (selectedCenter && !validCenters.some(c => String(c.id) === String(selectedCenter))) {
      setSelectedCenter('');
    }
  }, [selectedBranch, centers, selectedCenter]);

  // Fetch nominations based on filters
  const fetchNominations = async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = {
        branchId: selectedBranch || undefined,
        centerId: selectedCenter || undefined,
        status: selectedStatus || undefined
      };

      const nominations = await nominationService.getNominationsWithFilters(filters);
      setNominations(nominations);
    } catch (error) {
      console.error('Error fetching nominations:', error);
      setError('حدث خطأ أثناء تحميل المرشحين. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleBranchChange = (e) => {
    // Only allow branch change for admin users
    if (isCommittee) return;

    setSelectedBranch(e.target.value);
    setSelectedCenter('');
  };

  const handleCenterChange = (e) => {
    const val = e.target.value;
    console.debug('[Reports] Center changed ->', val);
    setSelectedCenter(val);
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  // Apply filters
  const handleApplyFilters = () => {
    console.debug('[Reports] Applying filters', { selectedBranch, selectedCenter, selectedStatus });
    fetchNominations();
    // Also refresh statistics when applying filters
    fetchStatistics();
    // Clear center table (user can reload per-center with new filters)
    setCenterRows([]);
  };

  // Reset filters
  const handleResetFilters = () => {
    // For committee users, don't reset branch filter
    if (!isCommittee) {
      setSelectedBranch('');
    }
    setSelectedCenter('');
    setSelectedStatus('');
    setNominations([]);
  };

  // Download individual nomination report
  const downloadNominationReport = async (nominationId, candidateName) => {
    try {
      setDownloadingNominationId(nominationId);
      // Use reportService to download the nomination report
      const singleFormat = reportFormat === 'xlsx' ? 'pdf' : reportFormat; // XLSX is bulk-only
      const response = await reportService.downloadNominationReport(nominationId, {
        includeEvaluation,
        includeAttachmentA,
        includeAttachmentB,
        format: singleFormat
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: (reportFormat === 'zip' ? 'application/zip' : 'application/pdf')
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Set filename based on format
      const extension = reportFormat === 'zip' ? 'zip' : 'pdf';
      link.download = `${candidateName.replace(/\s+/g, '_')}_report.${extension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'تم تنزيل التقرير بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error downloading report:', error);
      toast({
        title: 'حدث خطأ أثناء تنزيل التقرير',
        description: 'يرجى المحاولة مرة أخرى',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDownloadingNominationId(null);
    }
  };

  // Download bulk reports
  const downloadBulkReports = async () => {
    try {
      setBulkDownloading(true);
      // Use reportService to download bulk reports
      const filters = {
        branchId: selectedBranch || undefined,
        centerId: selectedCenter || undefined,
        status: selectedStatus || undefined
      };
      console.debug('[Reports] Bulk export filters', filters);

      const options = {
        includeEvaluation,
        includeAttachments: includeAttachmentA || includeAttachmentB,
        allFields,
        format: reportFormat
      };

      const response = await reportService.bulkExportReports(filters, options);

      // Create a blob URL and trigger download
      const blobType =
        reportFormat === 'pdf'
          ? 'application/pdf'
          : reportFormat === 'xlsx'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'application/zip';
      const blob = new Blob([response.data], { type: blobType });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      // Set filename based on format and filters
      const extension = reportFormat === 'pdf' ? 'pdf' : (reportFormat === 'xlsx' ? 'xlsx' : 'zip');
      const locationName = selectedCenter ? 
        (centers.find(c => String(c.id) === String(selectedCenter))?.name || 'all').replace(/\s+/g, '_') : 
        selectedBranch ? 
          (branches.find(b => String(b.id) === String(selectedBranch))?.name || 'all').replace(/\s+/g, '_') : 
          'all';

      link.download = `${locationName}_nominations_${new Date().toISOString().split('T')[0]}.${extension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'تم تنزيل التقارير بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error downloading bulk reports:', error);
      toast({
        title: 'حدث خطأ أثناء تنزيل التقارير',
        description: 'يرجى المحاولة مرة أخرى',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setBulkDownloading(false);
    }
  };

  // Centralized helpers
  const getStatusColor = (status) => getNominationStatusColor(status);
  const getStatusDisplay = (status) => getNominationStatusLabel(status);

  return (
    <>
      {/* Full-screen loading overlay for report generation/download */}
      {(bulkDownloading || !!downloadingNominationId) && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          bg="blackAlpha.600"
          zIndex={2000}
        >
          <Flex direction="column" align="center" justify="center" height="100%" color="white">
            <Spinner size="xl" thickness="4px" color="white" mb={4} />
            <Text fontSize="lg" fontWeight="bold">
              {bulkDownloading
                ? (reportFormat === 'zip'
                    ? 'جارٍ إنشاء الملفات وضغطها...'
                    : reportFormat === 'xlsx'
                      ? 'جارٍ إنشاء ملف Excel جماعي...'
                      : 'جارٍ إنشاء ملف PDF جماعي...')
                : (reportFormat === 'zip'
                    ? 'جارٍ إنشاء تقرير المرشح (ZIP)...'
                    : reportFormat === 'xlsx'
                      ? 'جارٍ إنشاء تقرير المرشح (PDF)...'
                      : 'جارٍ إنشاء تقرير المرشح (PDF)...')}
            </Text>
            <Text fontSize="sm" opacity={0.9} mt={2}>قد يستغرق ذلك بعض الوقت حسب عدد المرشحين وحجم الملفات.</Text>
          </Flex>
        </Box>
      )}

      <Box p={4}>
        <Heading mb={6}>التقارير</Heading>

        <Tabs variant="enclosed" colorScheme="primary" mb={6}>
          <TabList>
            <Tab>تقارير المرشحين</Tab>
            <Tab>تقارير إحصائية</Tab>
          </TabList>

          <TabPanels>
            {/* Nomination Reports Tab */}
            <TabPanel>
              <Stack spacing={6}>
                {/* Filters Card */}
                <Card>
                  <CardHeader>
                    <Heading size="md">تصفية المرشحين</Heading>
                  </CardHeader>
                  <CardBody>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
                      <GridItem>
                        <FormControl>
                          <FormLabel>الفرع</FormLabel>
                          <Select
                            value={selectedBranch}
                            onChange={handleBranchChange}
                            placeholder="اختر الفرع"
                            isDisabled={isCommittee}
                          >
                            {branches.map((branch) => (
                              <option key={branch.id} value={branch.id}>
                                {branch.name}
                              </option>
                            ))}
                          </Select>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl>
                          <FormLabel>المركز</FormLabel>
                          <Select
                            value={selectedCenter}
                            onChange={handleCenterChange}
                            placeholder="اختر المركز"
                            isDisabled={!selectedBranch}
                          >
                            {centers
                              .filter((center) => !selectedBranch || String(center.branchId) === String(selectedBranch))
                              .map((center) => (
                                <option key={center.id} value={center.id}>
                                  {center.name}
                                </option>
                              ))}
                          </Select>
                        </FormControl>
                      </GridItem>

                      <GridItem>
                        <FormControl>
                          <FormLabel>الحالة</FormLabel>
                          <Select
                            value={selectedStatus}
                            onChange={handleStatusChange}
                            placeholder="اختر الحالة"
                          >
                            <option value="draft">مسودة</option>
                            <option value="submitted">تحت التقييم</option>
                            <option value="pending">في انتظار النتيجة</option>
                            <option value="shortlisted">قبول أولي</option>
                            <option value="accepted">مقبول</option>
                            <option value="rejected">مرفوض</option>
                            <option value="returned">عائد</option>
                          </Select>
                        </FormControl>
                      </GridItem>
                    </Grid>

                    <Flex mt={4} justifyContent="flex-end" gap={3}>
                      <Button
                        colorScheme="gray"
                        onClick={handleResetFilters}
                      >
                        إعادة تعيين
                      </Button>
                      <Button
                        colorScheme="primary"
                        onClick={handleApplyFilters}
                        isLoading={loading}
                      >
                        تطبيق
                      </Button>
                    </Flex>
                  </CardBody>
                </Card>

                {/* Report Options Card */}
                <Card>
                  <CardHeader>
                    <Heading size="md">خيارات التقرير</Heading>
                  </CardHeader>
                  <CardBody>
                    <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                      <GridItem>
                        <Stack spacing={4}>
                          <Heading size="sm">محتوى التقرير</Heading>
                          <Checkbox
                            isChecked={includeEvaluation}
                            onChange={(e) => setIncludeEvaluation(e.target.checked)}
                          >
                            تضمين تقرير التقييم
                          </Checkbox>
                          <Checkbox
                            isChecked={includeAttachmentA}
                            onChange={(e) => setIncludeAttachmentA(e.target.checked)}
                          >
                            تضمين السيرة الذاتية
                          </Checkbox>
                          <Checkbox
                            isChecked={includeAttachmentB}
                            onChange={(e) => setIncludeAttachmentB(e.target.checked)}
                          >
                            تضمين المسح الأمني
                          </Checkbox>
                        </Stack>
                      </GridItem>

                      <GridItem>
                        <Stack spacing={4}>
                          <Heading size="sm">تنسيق التقرير</Heading>
                          <FormControl>
                            <Select
                              value={reportFormat}
                              onChange={(e) => setReportFormat(e.target.value)}
                            >
                              <option value="xlsx">ملف Excel (XLSX) - تصدير جماعي فقط</option>
                              <option value="pdf">ملف PDF واحد (مدمج)</option>
                              <option value="zip">ملفات منفصلة (مضغوطة)</option>
                            </Select>
                          </FormControl>
                          {reportFormat === 'xlsx' && (
                            <Checkbox
                              isChecked={allFields}
                              onChange={(e) => setAllFields(e.target.checked)}
                            >
                              تصدير جميع الحقول (شامل)
                            </Checkbox>
                          )}
                        </Stack>
                      </GridItem>
                    </Grid>

                    {(includeAttachmentA || includeAttachmentB) && (
                      <Alert status="info" mt={4}>
                        <AlertIcon />
                        <Text>
                          سيتم تضمين المرفقات المتاحة فقط. إذا لم تكن المرفقات متوفرة لمرشح معين، فلن يتم تضمينها في التقرير.
                        </Text>
                      </Alert>
                    )}
                  </CardBody>
                </Card>

                {/* Bulk Export Card */}
                {/* TEMP: allow committee members to see bulk export (toggle off later by reverting to admins-only) */}
                {currentUser && (currentUser.role === 'admin' || currentUser.role === 'committee') && (
                  <Card>
                    <CardHeader>
                      <Heading size="md">تصدير جماعي</Heading>
                    </CardHeader>
                    <CardBody>
                      <Text mb={4}>
                        يمكنك تصدير تقارير لجميع المرشحين المحددين في التصفية أعلاه.
                        {!selectedBranch && !selectedCenter && !selectedStatus && (
                          ' سيتم تصدير تقارير لجميع المرشحين.'
                        )}
                      </Text>

                      <Button
                        leftIcon={<FiArchive />}
                        colorScheme="primary"
                        onClick={downloadBulkReports}
                        isDisabled={loading || bulkDownloading}
                        isLoading={bulkDownloading}
                        loadingText={reportFormat === 'zip' ? 'جارٍ إنشاء الملفات وضغطها...' : (reportFormat === 'xlsx' ? 'جارٍ إنشاء ملف Excel...' : 'جارٍ إنشاء ملف PDF...')}
                      >
                        تصدير جماعي للتقارير
                      </Button>
                    </CardBody>
                  </Card>
                )}

                {/* Nominations List */}
                <Card>
                  <CardHeader>
                    <Heading size="md">قائمة المرشحين</Heading>
                  </CardHeader>
                  <CardBody>
                    {error && (
                      <Alert status="error" mb={4}>
                        <AlertIcon />
                        {error}
                      </Alert>
                    )}

                    {loading ? (
                      <Flex justify="center" align="center" py={10}>
                        <Spinner size="xl" color="primary.500" />
                      </Flex>
                    ) : nominations.length > 0 ? (
                      <Stack spacing={4} divider={<Divider />}>
                        {nominations.map((nomination, idx) => {
                          // Build candidate name robustly against various API shapes
                          const nameFromFields = [nomination?.first_name, nomination?.father_name, nomination?.last_name]
                            .filter(Boolean)
                            .join(' ')
                            .trim();
                          const candidateName = nomination?.candidate?.full_name
                            ? nomination.candidate.full_name
                            : (nameFromFields || nomination?.full_name || nomination?.applicant_name || 'غير معروف');
                          const applicationNumber = nomination?.application_number || nomination?.id || '-';
                          return (
                            <Flex
                              key={nomination?.id ?? idx}
                              justify="space-between"
                              align="center"
                              p={2}
                            >
                              <Box>
                                <Text fontWeight="bold">{candidateName}</Text>
                                <Text fontSize="sm" color="gray.600">
                                  رقم الطلب: {applicationNumber}
                                </Text>
                                <Badge colorScheme={getStatusColor(nomination?.status)}>
                                  {getStatusDisplay(nomination?.status)}
                                </Badge>
                              </Box>

                              <Button
                                leftIcon={<FiDownload />}
                                colorScheme="primary"
                                size="sm"
                                isLoading={downloadingNominationId === nomination?.id}
                                loadingText={reportFormat === 'zip' ? 'تحميل ZIP...' : 'تحميل PDF...'}
                                isDisabled={downloadingNominationId && downloadingNominationId !== nomination?.id}
                                onClick={() => downloadNominationReport(nomination?.id, candidateName)}
                              >
                                تنزيل التقرير
                              </Button>
                            </Flex>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Text textAlign="center" py={10} color="gray.500">
                        قم بتطبيق التصفية لعرض المرشحين
                      </Text>
                    )}
                  </CardBody>
                </Card>
              </Stack>
            </TabPanel>

            {/* Statistical Reports Tab */}
            <TabPanel>
              <Card>
                <CardHeader>
                  <Heading size="md">التقارير الإحصائية</Heading>
                </CardHeader>
                <CardBody>
                  <Text mb={4}>
                    اعرض الإحصائيات مباشرة داخل الصفحة (يتم تطبيق نفس فلاتر الفرع/المركز).
                  </Text>

                  <Stack spacing={4} direction={{ base: 'column', md: 'row' }} mb={6}>
                    <Button
                      leftIcon={<FiFileText />}
                      colorScheme="primary"
                      onClick={fetchStatistics}
                      isLoading={statsLoading}
                    >
                      عرض الإحصائيات
                    </Button>
                  </Stack>

                  {statsError && (
                    <Alert status="error" mb={6}>
                      <AlertIcon />
                      {statsError}
                    </Alert>
                  )}

                  {statsLoading && (
                    <Flex justify="center" align="center" py={10}>
                      <Spinner size="xl" color="primary.500" />
                    </Flex>
                  )}

                  {!statsLoading && (nominationsStats || evaluationsStats) && (
                    <Stack spacing={4}>
                      {/* Nominations status chart */}
                      {nominationsStats && (
                        <Box>
                          <Heading size="sm" mb={3}>توزيع حالات المرشحين</Heading>
                          <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap={6}>
                            <GridItem>
                              <Box height="280px" maxW="900px" width="100%" mx="auto">
                                <Bar
                                  data={{
                                  labels: ['مسودة', 'تحت التقييم', 'قيد المراجعة', 'قبول أولي', 'مرفوض', 'مقبول'],
                                  datasets: [
                                    {
                                      label: 'عدد الطلبات',
                                      data: [
                                        nominationsStats.status_counts?.draft ?? 0,
                                        nominationsStats.status_counts?.submitted ?? 0,
                                        nominationsStats.status_counts?.under_review ?? 0,
                                        nominationsStats.status_counts?.shortlisted ?? 0,
                                        nominationsStats.status_counts?.rejected ?? 0,
                                        nominationsStats.status_counts?.accepted ?? 0,
                                      ],
                                      backgroundColor: 'rgba(54, 162, 235, 0.6)'
                                    }
                                  ]
                                  }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: { legend: { display: false }, title: { display: false } },
                                    layout: { padding: 0 },
                                    scales: { x: { ticks: { maxRotation: 0 } }, y: { beginAtZero: true } }
                                  }}
                                />
                              </Box>
                            </GridItem>
                            <GridItem>
                              <Card variant="outline">
                                <CardHeader>
                                  <Heading size="sm">إحصائيات الحالات</Heading>
                                </CardHeader>
                                <CardBody>
                                  <Table size="sm" variant="simple">
                                    <Tbody>
                                      <Tr>
                                        <Td>مسودة</Td>
                                        <Td textAlign="center"><Text fontSize="md">{nominationsStats.status_counts?.draft ?? 0}</Text></Td>
                                      </Tr>
                                      <Tr>
                                        <Td>تحت التقييم</Td>
                                        <Td textAlign="center"><Text fontSize="md">{nominationsStats.status_counts?.submitted ?? 0}</Text></Td>
                                      </Tr>
                                      <Tr>
                                        <Td>قيد المراجعة</Td>
                                        <Td textAlign="center"><Text fontSize="md">{nominationsStats.status_counts?.under_review ?? 0}</Text></Td>
                                      </Tr>
                                      <Tr>
                                        <Td>قبول أولي</Td>
                                        <Td textAlign="center"><Text fontSize="md">{nominationsStats.status_counts?.shortlisted ?? 0}</Text></Td>
                                      </Tr>
                                      <Tr>
                                        <Td>مرفوض</Td>
                                        <Td textAlign="center"><Text fontSize="md">{nominationsStats.status_counts?.rejected ?? 0}</Text></Td>
                                      </Tr>
                                      <Tr>
                                        <Td>مقبول</Td>
                                        <Td textAlign="center"><Text fontSize="md">{nominationsStats.status_counts?.accepted ?? 0}</Text></Td>
                                      </Tr>
                                    </Tbody>
                                  </Table>
                                </CardBody>
                              </Card>
                            </GridItem>
                          </Grid>
                        </Box>
                      )}

                      {/* Evaluations recommendations chart + averages table */}
                      {evaluationsStats && (
                        <Box>
                          <Heading size="sm" mb={3}>توصيات التقييم ومتوسط الدرجات</Heading>
                          <Grid templateColumns={{ base: '1fr', md: '2fr 1fr' }} gap={6}>
                            <GridItem>
                              <Box height="280px" maxW="900px" width="100%" mx="auto">
                                <Bar
                                  data={{
                                  labels: ['موصى به جداً', 'موصى به', 'محايد', 'غير موصى به', 'غير موصى به إطلاقاً'],
                                  datasets: [
                                    {
                                      label: 'عدد التقييمات',
                                      data: [
                                        evaluationsStats.recommendation_counts?.highly_recommended ?? 0,
                                        evaluationsStats.recommendation_counts?.recommended ?? 0,
                                        evaluationsStats.recommendation_counts?.neutral ?? 0,
                                        evaluationsStats.recommendation_counts?.not_recommended ?? 0,
                                        evaluationsStats.recommendation_counts?.strongly_not_recommended ?? 0,
                                      ],
                                      backgroundColor: 'rgba(255, 159, 64, 0.6)'
                                    }
                                  ]
                                  }}
                                  options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: { legend: { display: false }, title: { display: false } },
                                    layout: { padding: 0 },
                                    scales: { x: { ticks: { maxRotation: 0 } }, y: { beginAtZero: true } }
                                  }}
                                />
                              </Box>
                            </GridItem>
                            <GridItem>
                              <Card variant="outline">
                                <CardHeader>
                                  <Heading size="sm">متوسط الدرجات</Heading>
                                </CardHeader>
                                <CardBody>
                                  <Stack spacing={2}>
                                    <Flex justify="space-between"><Text>معلومات المرشح عن الجمعية</Text><Badge>{evaluationsStats.average_scores?.society_knowledge_score ?? 0}</Badge></Flex>
                                    <Flex justify="space-between"><Text>المسؤولية الاجتماعية</Text><Badge>{evaluationsStats.average_scores?.social_responsibility_score ?? 0}</Badge></Flex>
                                    <Flex justify="space-between"><Text>الإسهام في رؤية 2030</Text><Badge>{evaluationsStats.average_scores?.vision_2030_score ?? 0}</Badge></Flex>
                                    <Flex justify="space-between"><Text>خطة تفعيل المجلس وتنمية الموارد</Text><Badge>{evaluationsStats.average_scores?.center_plan_score ?? 0}</Badge></Flex>
                                    <Divider />
                                    <Flex justify="space-between"><Text fontWeight="bold">المعدل العام</Text><Badge colorScheme="green">{evaluationsStats.average_scores?.overall ?? 0}</Badge></Flex>
                                  </Stack>
                                </CardBody>
                              </Card>
                            </GridItem>
                          </Grid>
                        </Box>
                      )}
                      {/* Per-center statistics table (nominations only) */}
                      <Box>
                        <Heading size="sm" mb={3}>جدول الإحصائيات حسب المركز</Heading>
                        <Stack spacing={4} direction={{ base: 'column', md: 'row' }} mb={2}>
                          <Button
                            leftIcon={<FiFileText />}
                            colorScheme="primary"
                            onClick={fetchCenterStatistics}
                            isLoading={centerStatsLoading}
                          >
                            تحميل جدول المراكز
                          </Button>
                        </Stack>
                        {centerStatsError && (
                          <Alert status="error" mb={4}>
                            <AlertIcon />
                            {centerStatsError}
                          </Alert>
                        )}
                        {!centerStatsLoading && centerRows.length > 0 && (
                          <Box overflowX="auto">
                            <Table size="md" variant="striped" colorScheme="gray">
                              <Thead>
                                <Tr>
                                  <Th>المركز</Th>
                                  <Th textAlign="center">إجمالي الترشيحات</Th>
                                  <Th textAlign="center">مسودة</Th>
                                  <Th textAlign="center">تحت التقييم</Th>
                                  <Th textAlign="center">قيد المراجعة</Th>
                                  <Th textAlign="center">قبول أولي</Th>
                                  <Th textAlign="center">مرفوض</Th>
                                  <Th textAlign="center">مقبول</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {centerRows.map((r) => (
                                  <Tr key={r.id}>
                                    <Td>{r.name}</Td>
                                    <Td textAlign="center"><Text fontSize="lg" fontWeight="bold">{r.nom_total}</Text></Td>
                                    <Td textAlign="center"><Text fontSize="lg">{r.draft}</Text></Td>
                                    <Td textAlign="center"><Text fontSize="lg">{r.submitted}</Text></Td>
                                    <Td textAlign="center"><Text fontSize="lg">{r.under_review}</Text></Td>
                                    <Td textAlign="center"><Text fontSize="lg">{r.shortlisted}</Text></Td>
                                    <Td textAlign="center"><Text fontSize="lg">{r.rejected}</Text></Td>
                                    <Td textAlign="center"><Text fontSize="lg">{r.accepted}</Text></Td>
                                  </Tr>
                                ))}
                              </Tbody>
                              <Tfoot>
                                <Tr>
                                  <Th>الإجمالي</Th>
                                  <Th textAlign="center"><Text as="span" fontWeight="bold" fontSize="lg">{centerTotals.nom_total}</Text></Th>
                                  <Th textAlign="center"><Text as="span" fontWeight="bold" fontSize="lg">{centerTotals.draft}</Text></Th>
                                  <Th textAlign="center"><Text as="span" fontWeight="bold" fontSize="lg">{centerTotals.submitted}</Text></Th>
                                  <Th textAlign="center"><Text as="span" fontWeight="bold" fontSize="lg">{centerTotals.under_review}</Text></Th>
                                  <Th textAlign="center"><Text as="span" fontWeight="bold" fontSize="lg">{centerTotals.shortlisted}</Text></Th>
                                  <Th textAlign="center"><Text as="span" fontWeight="bold" fontSize="lg">{centerTotals.rejected}</Text></Th>
                                  <Th textAlign="center"><Text as="span" fontWeight="bold" fontSize="lg">{centerTotals.accepted}</Text></Th>
                                </Tr>
                              </Tfoot>
                            </Table>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  )}
                </CardBody>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </>
  );
};

export default Reports;
